import React, { useState } from "react";
import { format, parseISO } from "date-fns";
import { Button } from "../../Components/ui/button";
import { SkeletonTable } from "../../Components/ui/skeleton";
import { Modal, ModalContent } from "../../Components/ui/modal";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../../Components/ui/tabs";
import Spinner from "../../Assets/spinner";
import toast from "react-hot-toast";
import {
  useGetVendorByIdQuery,
  useDeactivateVendorMutation,
  useReactivateVendorMutation,
  useApproveVendorMutation,
  useDeclineVendorMutation,
} from "./VendorApi";
import VendorItems from "./VendorItems";

const Field = ({ label, children }) => (
  <div>
    <p className='text-xs text-gray-400 font-medium'>{label}</p>
    <p className='text-primaryBlack font-normal text-sm break-words'>
      {children}
    </p>
  </div>
);

const VendorDetail = ({ id }) => {
  const [declineOpen, setDeclineOpen] = useState(false);
  const { data, isLoading } = useGetVendorByIdQuery(id);
  const [deactivateVendor, { isLoading: deactivating }] =
    useDeactivateVendorMutation();
  const [reactivateVendor, { isLoading: reactivating }] =
    useReactivateVendorMutation();
  const [approveVendor, { isLoading: approving }] = useApproveVendorMutation();
  const [declineVendor, { isLoading: declining }] = useDeclineVendorMutation();

  const vendor = data?.data;

  const handleDeactivate = async () => {
    try {
      const res = await deactivateVendor(id).unwrap();
      if (res?.statusCode < 300) {
        toast.success("Vendor deactivated", { id: "vendorDeactivate" });
      }
    } catch {
      toast.error("Action failed, please try again", { id: "vendorErr" });
    }
  };

  const handleReactivate = async () => {
    try {
      const res = await reactivateVendor(id).unwrap();
      if (res?.statusCode < 300) {
        toast.success("Vendor reactivated", { id: "vendorReactivate" });
      }
    } catch {
      toast.error("Action failed, please try again", { id: "vendorErr" });
    }
  };

  const handleApprove = async () => {
    try {
      const res = await approveVendor(id).unwrap();
      if (res?.statusCode < 300) {
        toast.success("Vendor approved", { id: "vendorApprove" });
      }
    } catch {
      toast.error("Action failed, please try again", { id: "vendorErr" });
    }
  };

  const handleDecline = async () => {
    try {
      const res = await declineVendor(id).unwrap();
      if (res?.statusCode < 300) {
        toast.success("Vendor declined", { id: "vendorDecline" });
        setDeclineOpen(false);
      }
    } catch {
      toast.error("Action failed, please try again", { id: "vendorErr" });
    }
  };

  if (isLoading)
    return (
      <div className='w-full'>
        <SkeletonTable />
      </div>
    );

  return (
    <div className='w-full'>
      <Tabs defaultValue='details'>
        <TabsList>
          <TabsTrigger value='details'>Vendor Details</TabsTrigger>
          <TabsTrigger value='business'>Business Details</TabsTrigger>
          <TabsTrigger value='items'>Items</TabsTrigger>
        </TabsList>

        <TabsContent value='details'>
          <div className='grid md:grid-cols-3 gap-5 p-2'>
            <Field label='Business Name'>
              {vendor?.businessName || vendor?.name || "N/A"}
            </Field>
            <Field label='Vendor Code'>{vendor?.vendorCode || "N/A"}</Field>
            <Field label='Type'>
              <span className='capitalize'>{vendor?.type || "N/A"}</span>
            </Field>
            <Field label='Email'>
              <span className='lowercase'>{vendor?.email || "N/A"}</span>
            </Field>
            <Field label='Phone Number'>{vendor?.phoneNumber || "N/A"}</Field>
            <Field label='Contact Person Phone'>
              {vendor?.contactPersonPhoneNumber || "N/A"}
            </Field>
            {vendor?.ownerName && (
              <Field label='Owner Name'>
                <span className='capitalize'>{vendor.ownerName}</span>
              </Field>
            )}
            {vendor?.ownerEmail && (
              <Field label='Owner Email'>
                <span className='lowercase'>{vendor.ownerEmail}</span>
              </Field>
            )}
            <Field label='Approval Status'>
              <span className='capitalize'>{vendor?.status || "N/A"}</span>
            </Field>
            <Field label='Status'>
              {vendor?.activated ? "Active" : "Inactive"}
            </Field>
            <Field label='Email Verified'>
              {vendor?.emailVerified ? "Yes" : "No"}
            </Field>
            <Field label='Registered'>
              {vendor?.createdAt
                ? format(parseISO(vendor.createdAt), "LLL dd, y")
                : "N/A"}
            </Field>
          </div>
        </TabsContent>

        <TabsContent value='business'>
          <div className='grid md:grid-cols-3 gap-5 p-2'>
            {vendor?.logoFilePath ? (
              <div className='md:col-span-3'>
                <p className='text-xs text-gray-400 font-medium mb-1'>Logo</p>
                <img
                  src={`${process.env.REACT_APP_BASE_URL}file/asset/${vendor.logoFilePath}`}
                  alt='Vendor logo'
                  className='h-16 w-16 rounded-md object-cover border border-gray-100'
                />
              </div>
            ) : (
              <Field label='Logo'>N/A</Field>
            )}
            <Field label='Description'>{vendor?.description || "N/A"}</Field>
            <Field label='Slug'>{vendor?.slug || "N/A"}</Field>
            <Field label='Address'>{vendor?.address || "N/A"}</Field>
            <Field label='Website'>
              {vendor?.website ? (
                <a
                  href={vendor.website}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='text-primaryGreen underline break-all'
                >
                  {vendor.website}
                </a>
              ) : (
                "N/A"
              )}
            </Field>
            <Field label='Opening Time'>{vendor?.openingTime || "N/A"}</Field>
            <Field label='Closing Time'>{vendor?.closingTime || "N/A"}</Field>
            <Field label='Business Document'>
              {vendor?.businessDocument ? (
                <a
                  href={`${process.env.REACT_APP_BASE_URL}file/asset/${vendor.businessDocument}`}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='text-primaryGreen underline'
                >
                  View Document
                </a>
              ) : (
                "N/A"
              )}
            </Field>
          </div>
        </TabsContent>

        <TabsContent value='items'>
          <VendorItems vendorId={id} />
        </TabsContent>
      </Tabs>

      <div className='border-t border-gray-100 mt-4 pt-4 flex justify-end gap-2'>
        {vendor?.status === "pending" && (
          <>
            <Button
              variant='successful'
              onClick={handleApprove}
              disabled={approving}
            >
              {approving ? <Spinner /> : "Approve"}
            </Button>
            <Button variant='outline' onClick={() => setDeclineOpen(true)}>
              Decline
            </Button>
          </>
        )}
        {vendor?.status === "approved" &&
          (vendor?.activated ? (
            <Button
              variant='failed'
              onClick={handleDeactivate}
              disabled={deactivating}
            >
              {deactivating ? <Spinner /> : "Deactivate Vendor"}
            </Button>
          ) : (
            <Button
              variant='successful'
              onClick={handleReactivate}
              disabled={reactivating}
            >
              {reactivating ? <Spinner /> : "Reactivate Vendor"}
            </Button>
          ))}
      </div>

      <Modal open={declineOpen} onOpenChange={setDeclineOpen}>
        <ModalContent name='Decline Vendor?'>
          <p className='text-sm text-primaryBlack my-4'>
            Are you sure you want to decline this vendor? This action cannot be
            undone.
          </p>
          <div className='flex justify-between items-center bg-white w-[100%]'>
            <Button
              variant='successful'
              className='w-[30%]'
              onClick={handleDecline}
              disabled={declining}
            >
              {declining ? <Spinner /> : "Yes"}
            </Button>
            <Button
              variant='failed'
              className='w-[30%]'
              onClick={() => setDeclineOpen(false)}
            >
              No
            </Button>
          </div>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default VendorDetail;
